import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ActivityDirectory, ActivityFilters, ActivityReport, ActivityService } from '../../shared/services/activity.service';
import { ActivityRankingComponent, RankingEntry } from './activity-ranking.component';
import { ActivityDay, ActivityTrendChartComponent } from './activity-trend-chart.component';

@Component({
  selector: 'app-activity-insights',
  standalone: true,
  imports: [CommonModule, FormsModule, TableModule, ActivityRankingComponent, ActivityTrendChartComponent],
  templateUrl: './activity-insights.component.html',
  styleUrls: ['./activity-insights.component.scss'],
})
export class ActivityInsightsComponent implements OnInit, OnDestroy {
  directory: ActivityDirectory = { users: [], mandants: [], tiles: [] };
  report?: ActivityReport;
  loading = false;
  error = '';
  from = this.dateInput(new Date(Date.now() - 29 * 86400000));
  to = this.dateInput(new Date());
  mandantId = '';
  userId = '';
  tileId = '';
  kind = '';
  eventPage = 0;
  clusterPage = 0;
  inactivityDays = 90;
  live = false;
  updatedAt?: Date;
  days: ActivityDay[] = [];
  topTiles: RankingEntry[] = [];
  topMandants: RankingEntry[] = [];
  readonly periods = [7, 30, 90, 365];
  filtersOpen = false;
  /** SVG-Polylinien (viewBox 100×32) für die Mini-Verläufe in den Kennzahl-Karten. */
  sparklines = { opens: '', visits: '' };
  peakDay?: { date: string; total: number };
  private timer?: ReturnType<typeof setInterval>;
  private destroyed = false;
  private appliedFilters?: ActivityFilters;

  constructor(private activity: ActivityService, private cdr: ChangeDetectorRef) {}

  async ngOnInit(): Promise<void> {
    await this.refresh();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    clearInterval(this.timer);
  }

  private dateInput(date: Date): string {
    return date.toISOString().slice(0, 10);
  }

  async refresh(resetPages = true): Promise<void> {
    if (this.loading) return;
    this.loading = true;
    this.error = '';
    try {
      const start = new Date(`${this.from}T00:00:00Z`);
      const end = new Date(`${this.to}T00:00:00Z`);
      end.setUTCDate(end.getUTCDate() + 1);
      if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) ||
        end <= start || end.getTime() - start.getTime() > 366 * 86400000) {
        throw new Error($localize`:@@activityInvalidRange:Bitte einen gültigen Zeitraum von maximal 366 Tagen auswählen.`);
      }
      if (resetPages) { this.eventPage = 0; this.clusterPage = 0; }
      const filters: ActivityFilters = {
        from: start.toISOString(), to: end.toISOString(), mandantId: this.mandantId,
        userId: this.userId, tileId: this.tileId, kind: this.kind,
        eventPage: this.eventPage, clusterPage: this.clusterPage,
      };
      const previousFilters = this.appliedFilters;
      if (previousFilters && (['from', 'to', 'mandantId', 'userId', 'tileId', 'kind'] as const)
        .some(key => filters[key] !== previousFilters[key])) {
        this.eventPage = this.clusterPage = filters.eventPage = filters.clusterPage = 0;
      }
      const [directory, report] = await Promise.all([this.activity.directory(), this.activity.report(filters)]);
      if (this.destroyed) return;
      this.directory = directory;
      this.report = report;
      this.appliedFilters = filters;
      this.updatedAt = new Date();
      const daily = new Map(report.daily.map(day => [day._id, day]));
      this.days = [];
      for (let time = start.getTime(); time < end.getTime(); time += 86400000) {
        const date = new Date(time).toISOString().slice(0, 10);
        const day = daily.get(date);
        this.days.push({ date, opens: day?.opens ?? 0, visits: day?.visits ?? 0 });
      }
      this.sparklines = { opens: this.sparkline('opens'), visits: this.sparkline('visits') };
      this.topTiles = report.byTile.slice(0, 10).map(entry => ({ id: entry._id, name: this.tileName(entry._id), count: entry.count }));
      this.topMandants = report.byMandant.map(entry => ({ id: entry._id, name: this.mandantName(entry._id), count: entry.count }));
      this.peakDay = this.days.reduce<typeof this.peakDay>((peak, day) =>
        day.opens + day.visits > (peak?.total ?? 0) ? { date: day.date, total: day.opens + day.visits } : peak, undefined);
    } catch (error) {
      console.error('Failed to load portal insights', error);
      this.report = undefined;
      this.error = error instanceof Error && error.message.includes('366')
        ? error.message : $localize`:@@activityLoadError:Die Nutzungsdaten konnten nicht geladen werden. Bitte erneut versuchen.`;
    } finally {
      this.loading = false;
      if (!this.destroyed) this.cdr.detectChanges();
    }
  }

  private sparkline(key: 'opens' | 'visits'): string {
    const max = Math.max(1, ...this.days.map(day => day[key]));
    const step = 100 / Math.max(1, this.days.length - 1);
    return this.days.map((day, i) => `${(i * step).toFixed(2)},${(30 - day[key] / max * 28).toFixed(2)}`).join(' ');
  }

  /** Aktiver Zeitraum-Preset, falls der angewendete Zeitraum genau den letzten N Tagen entspricht. */
  get activePeriod(): number | null {
    const today = this.dateInput(new Date());
    return this.periods.find(days => this.to === today &&
      this.from === this.dateInput(new Date(Date.now() - (days - 1) * 86400000))) ?? null;
  }

  /** Angewendete Filter als entfernbare Chips — macht Drill-downs aus den Ranglisten sichtbar. */
  get activeFilters(): { key: 'mandantId' | 'userId' | 'tileId' | 'kind'; label: string; value: string }[] {
    const applied = this.appliedFilters;
    if (!applied) return [];
    const chips: { key: 'mandantId' | 'userId' | 'tileId' | 'kind'; label: string; value: string }[] = [];
    if (applied.mandantId) chips.push({ key: 'mandantId', label: $localize`:@@activityTenant:Mandant`, value: this.mandantName(applied.mandantId) });
    if (applied.userId) chips.push({ key: 'userId', label: $localize`:@@activityUser:Benutzer`, value: this.userName(applied.userId) });
    if (applied.tileId) chips.push({ key: 'tileId', label: $localize`:@@activityTile:Kachel`, value: this.tileName(applied.tileId) });
    if (applied.kind) chips.push({
      key: 'kind', label: $localize`:@@activityEvent:Ereignis`,
      value: applied.kind === 'tile_open' ? $localize`:@@activityOpen:Kachel-Aufruf` : $localize`:@@activityVisit:Portalbesuch`,
    });
    return chips;
  }

  clearFilter(key: 'mandantId' | 'userId' | 'tileId' | 'kind'): void {
    this[key] = '';
    void this.refresh();
  }

  perDay(value: number): number {
    return Math.round(value / Math.max(1, this.days.length) * 10) / 10;
  }

  share(count: number, total: number): number {
    return total > 0 ? count / total * 100 : 0;
  }

  toggleLive(): void {
    clearInterval(this.timer);
    if (this.live) this.timer = setInterval(() => { void this.refresh(false); }, 60000);
  }

  setPeriod(days: number): void {
    this.to = this.dateInput(new Date());
    this.from = this.dateInput(new Date(Date.now() - (days - 1) * 86400000));
    void this.refresh();
  }

  changePage(type: 'events' | 'clusters', direction: number): void {
    if (type === 'events') this.eventPage += direction;
    else this.clusterPage += direction;
    void this.refresh(false);
  }

  get inactiveUsers(): ActivityDirectory['users'] {
    const cutoff = Date.now() - this.inactivityDays * 86400000;
    const selected = this.appliedFilters;
    return this.directory.users.filter(user =>
      (!selected?.mandantId || user.mandantIds.includes(selected.mandantId)) &&
      (!selected?.userId || user.id === selected.userId) &&
      (user.lastSignInAt === null || user.lastSignInAt < cutoff))
      .sort((a, b) => (a.lastSignInAt ?? 0) - (b.lastSignInAt ?? 0));
  }

  get quietTiles(): { id: string; name: string; count: number }[] {
    const selected = this.appliedFilters;
    const counts = new Map(this.report?.byTile.map(tile => [tile._id, tile.count]));
    return this.directory.tiles
      .filter(tile => (!selected?.mandantId || tile.mandantIds.includes(selected.mandantId)) &&
        (!selected?.tileId || tile.id === selected.tileId))
      .map(tile => ({ id: tile.id, name: tile.name, count: counts.get(tile.id) ?? 0 }))
      .sort((a, b) => a.count - b.count || a.name.localeCompare(b.name)).slice(0, 10);
  }

  drillDown(type: 'mandant' | 'tile', id: string | null): void {
    if (!id) return;
    if (type === 'mandant') this.mandantId = id;
    else this.tileId = id;
    void this.refresh();
  }

  reset(): void {
    this.mandantId = this.userId = this.tileId = this.kind = '';
    this.setPeriod(30);
  }

  mandantName(id: string | null): string {
    return id === null ? $localize`:@@activityNoTenant:Ohne Mandant` :
      this.directory.mandants.find(item => item.id === id)?.name ?? id;
  }

  userName(id: string): string {
    return this.directory.users.find(item => item.id === id)?.name ?? id;
  }

  tileName(id: string | null): string {
    return this.directory.tiles.find(item => item.id === id)?.name ?? id ?? '';
  }
}
